import React, { useEffect, useState } from 'react';
import { Alert, CircularProgress, Typography, Paper } from '@mui/material';
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from 'recharts';
import { API_URL } from '../config/api';
import { authenticatedFetch } from '../utils/authenticatedFetch';
import {
  buildHydraulicRequestUrl,
  isHydraulicSourceStale,
} from '../utils/hydraulic';

interface Props {
  icestupaId: string;
  startDate: string;
  endDate: string;
  sourceEndDate?: string;
}

interface HydraulicData {
  timestamp: string;
  icestupa_id: string;
  pressure: number;
  flowrate: number;
  [key: string]: any;
}

const DashboardMyriotaTab: React.FC<Props> = ({
  icestupaId,
  startDate,
  endDate,
  sourceEndDate = '',
}) => {
  const [data, setData] = useState<HydraulicData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dateWarning, setDateWarning] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    setDateWarning(null);
    const url = buildHydraulicRequestUrl(
      API_URL,
      icestupaId,
      startDate,
      endDate
    );
    authenticatedFetch(url)
      .then(res => {
        if (!res.ok) {
          return res.json().then(err => { throw err; });
        }
        return res.json();
      })
      .then(setData)
      .catch(e => {
        if (e && e.detail && e.detail.includes('365 days')) {
          setDateWarning('Date range cannot exceed 365 days.');
        } else {
          setError(e.message || 'Unknown error');
        }
      })
      .finally(() => setLoading(false));
  }, [icestupaId, startDate, endDate]);

  // Group data by icestupa_id for multi-line charts, and sort by timestamp ascending
  const grouped = data.reduce((acc, row) => {
    if (!acc[row.icestupa_id]) acc[row.icestupa_id] = [];
    acc[row.icestupa_id].push(row);
    return acc;
  }, {} as Record<string, HydraulicData[]>);
  Object.keys(grouped).forEach(id => {
    grouped[id].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
  });

  // Build a unified, sorted list of unique timestamps
  const allTimestamps = Array.from(new Set(data.map(d => d.timestamp)))
    .sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

  // Build a chartData array: one object per timestamp, with each stupa's values as keys
  const chartData = allTimestamps.map(ts => {
    const entry: any = { timestamp: ts };
    Object.keys(grouped).forEach(id => {
      const found = grouped[id].find(d => d.timestamp === ts);
      entry[`${id}_pressure`] = found ? found.pressure : null;
      entry[`${id}_flowrate`] = found ? found.flowrate : null;
    });
    return entry;
  });

  // X axis: timestamp, Y axis: pressure/flowrate, one line per icestupa_id
  return (
    <Paper sx={{ p: 2 }}>
      {/* Date range warning */}
      {dateWarning && (
        <Typography variant="caption" color="warning.main" sx={{ mb: 1, display: 'block' }}>
          {dateWarning}
        </Typography>
      )}
      {isHydraulicSourceStale(sourceEndDate) && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Calibrated hydraulic data currently ends on {sourceEndDate}.
        </Alert>
      )}
      {loading && <CircularProgress />}
      {error && <Typography color="error">{error}</Typography>}
      {!loading && !error && (
        <>
          {!data.length ? (
            <Alert severity="info">
              No calibrated hydraulic observations are available for this
              glacier and date range.
            </Alert>
          ) : (
            <>
              <Typography variant="h6" gutterBottom>Calibrated pressure over time</Typography>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="timestamp" type="category" tickFormatter={t => t.slice(5, 16)} />
                  <YAxis
                    label={{
                      value: 'Calibrated pressure',
                      angle: -90,
                      position: 'insideLeft',
                    }}
                  />
                  <Tooltip />
                  <Legend />
                  {Object.keys(grouped).map((id, idx) => (
                    <Line
                      key={id}
                      type="monotone"
                      dataKey={`${id}_pressure`}
                      name={id}
                      stroke={`hsl(${(idx * 60) % 360}, 70%, 50%)`}
                      dot={false}
                      isAnimationActive={false}
                      connectNulls
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>

              <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>Flowrate by Source</Typography>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="timestamp" type="category" tickFormatter={t => t.slice(5, 16)} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  {Object.keys(grouped).map((id, idx) => (
                    <Line
                      key={id}
                      type="monotone"
                      dataKey={`${id}_flowrate`}
                      name={id}
                      stroke={`hsl(${(idx * 60) % 360}, 70%, 50%)`}
                      dot={false}
                      isAnimationActive={false}
                      connectNulls
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </>
          )}
        </>
      )}
    </Paper>
  );
};

export default DashboardMyriotaTab; 